import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import search from "../../../assets/search.png";
import "./TaskReviewPage.css";

type ReviewAnswer = {
  answerId: string | number;
  text: string;
  correct: boolean;
};

type ReviewQuestion = {
  questionId: string | number;
  number?: number;
  text: string;
  type: "checkbox" | "radio";
  result?: string;
  selectedAnswers: string[];
  correctAnswers: string[];
  isCorrect: boolean;
  answers: ReviewAnswer[];
};

type ReviewTask = {
  id: string | number;
  order?: number;
  questions: ReviewQuestion[];
};

type TaskReviewData = {
  score: number;
  total: number;
  startedAt?: string;
  finishedAt?: string;
  date?: string;
  attemptId: string;
  taskAttemptId?: string;
  taskIndex?: number | null;
  taskNumber?: number | null;
  totalTasks?: number;
  isFinalTask?: boolean;
  tasks: ReviewTask[];
};

const getAnswerKey = (answer: ReviewAnswer) =>
  String(answer.answerId ?? answer.text);

const MOSCOW_TIME_ZONE = "Europe/Moscow";

const formatCourseDate = (value?: string | null) => {
  const date = value ? new Date(value) : new Date();
  return Number.isNaN(date.getTime())
    ? new Date().toLocaleDateString("ru-RU", { timeZone: MOSCOW_TIME_ZONE })
    : date.toLocaleDateString("ru-RU", { timeZone: MOSCOW_TIME_ZONE });
};

const formatMoscowDateTime = (value?: string | null) =>
  value
    ? new Date(value).toLocaleString("ru-RU", { timeZone: MOSCOW_TIME_ZONE })
    : "";

const TaskReviewPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const questionRefs = useRef<HTMLDivElement[]>([]);
  const [review, setReview] = useState<TaskReviewData | null>(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  const state = location.state as { attemptId?: string } | null;
  const queryAttemptId = new URLSearchParams(location.search).get("attemptId");
  const isAdminReview =
    location.pathname.startsWith("/admin/") ||
    location.pathname.startsWith("/superadmin/");
  const attemptId =
    state?.attemptId ||
    queryAttemptId ||
    localStorage.getItem("lastTaskAttemptId") ||
    localStorage.getItem("taskAttemptId") ||
    "";

  useEffect(() => {
    localStorage.removeItem("timeLeft");

    const loadReview = async () => {
      if (!attemptId) {
        setErrorMessage("Нет данных для отображения.");
        setIsLoading(false);
        return;
      }

      try {
        const response = await fetch(
          `/api/task-review/${attemptId}`
        );
        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.error || "Не удалось загрузить обзор");
        }

        setReview(data);
      } catch (error) {
        const message =
          error instanceof Error ? error.message : "Не удалось загрузить обзор";
        setErrorMessage(message);
      } finally {
        setIsLoading(false);
      }
    };

    loadReview();
  }, [attemptId]);

  const questions = useMemo(
    () => review?.tasks.flatMap((task) => task.questions) || [],
    [review]
  );

  const scrollToQuestion = (index: number) => {
    questionRefs.current[index]?.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });
  };

  const calculateElapsedTime = (startTimeISO?: string, endTimeISO?: string) => {
    if (!startTimeISO || !endTimeISO) {
      return { minutes: 0, seconds: 0 };
    }

    const startTime = new Date(startTimeISO).getTime();
    const endTime = new Date(endTimeISO).getTime();
    const elapsedSeconds = Math.max(0, Math.floor((endTime - startTime) / 1000));

    return {
      minutes: Math.floor(elapsedSeconds / 60),
      seconds: elapsedSeconds % 60,
    };
  };

  const handleFinishSituation = () => {
    if (isAdminReview) {
      navigate(
        localStorage.getItem("role") === "superadmin"
          ? "/superadmin/results"
          : "/admin/results"
      );
      return;
    }

    navigate("/task-result", {
      state: {
        attemptId: review?.attemptId || attemptId,
        taskAttemptId: review?.taskAttemptId || review?.attemptId || attemptId,
        taskIndex: review?.taskIndex,
        isFinalTask: review?.isFinalTask,
        totalTasks: review?.totalTasks,
      },
    });
  };

  if (isLoading) {
    return <div className="task-review-page">Загрузка обзора...</div>;
  }

  if (errorMessage || !review) {
    return (
      <div className="task-review-page">
        {errorMessage || "Нет данных для отображения."}
      </div>
    );
  }

  const startTime = review.startedAt || "";
  const endTime = review.finishedAt || review.date || "";
  const { minutes, seconds } = calculateElapsedTime(startTime, endTime);

  return (
    <div className="task-review-page">
      <div className="title"></div>
      <div
        onClick={() => {
          localStorage.clear();
          navigate("/");
        }}
        className="gohome"
      >
        Выход
      </div>
      <div className="title22">
        <div>Решение ситуационных задач - 3-й этап (Мультикейсы)</div>
        <div className="imgCont">
          <img src={search} alt="" style={{ width: 20 }} />
        </div>
      </div>

      <div className="task-review-breadcrumb">
        Мои курсы › Решение ситуационных задач - 3-й этап (Мультикейсы)
        {" "}{formatCourseDate(startTime)}
      </div>

      <div className="main-content">
        <div className="all_rev_cont">
          <div className="report1">
            <div>
              <b>Тест начат:</b>{" "}
              {startTime
                ? formatMoscowDateTime(startTime)
                : "Неизвестно"}
            </div>
            <div>
              <b>Состояние:</b> Завершен
            </div>
            <div>
              <b>Завершен:</b>{" "}
              {endTime ? formatMoscowDateTime(endTime) : "Неизвестно"}
            </div>
            <div>
              <b>Прошло времени:</b> {minutes} мин. {seconds} сек.
            </div>
          </div>

          {questions.map((question, qIndex) => (
            <div
              key={`${question.questionId}-${qIndex}`}
              ref={(el) => {
                if (el) questionRefs.current[qIndex] = el;
              }}
              className="question_review_cont"
            >
              <div className="question-title">
                <div>
                  Вопрос <b>{qIndex + 1}</b>
                </div>
                <div>{question.isCorrect ? "Верно" : "Неверно"}</div>
                <div>Балл: {question.isCorrect ? "1,00" : "0,00"}</div>
              </div>

              <div className="one_more_cont">
                <div className="task_cont">
                  <strong>{question.text}</strong>
                  <div className="review-answers-list">
                    {question.answers.map((answer, answerIndex) => {
                      const answerKey = getAnswerKey(answer);
                      const selected = question.selectedAnswers
                        .map(String)
                        .includes(answerKey);
                      const correct = question.correctAnswers
                        .map(String)
                        .includes(answerKey);
                      const optionLetter = String.fromCharCode(65 + answerIndex);

                      return (
                        <div
                          key={answerKey}
                          className={`answer-item ${
                            correct
                              ? "correct-answer"
                              : selected
                              ? "wrong-answer"
                              : ""
                          }`}
                        >
                          <span
                            className={`review-choice-marker ${
                              question.type === "checkbox" ? "checkbox" : "radio"
                            } ${selected ? "selected" : ""}`}
                            aria-hidden="true"
                          />
                          <span className="answer-label">
                            {optionLetter}. {answer.text}
                          </span>
                          {correct && (
                            <span className="answer-mark correct-mark" aria-hidden="true" />
                          )}
                          {!correct && selected && (
                            <span className="answer-mark wrong-mark" aria-hidden="true" />
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="correct_message">
                  {question.isCorrect
                    ? "Ваш ответ верный."
                    : "Ваш ответ неправильный."}
                </div>
              </div>
            </div>
          ))}

          <div className="task-review-actions">
            <button onClick={handleFinishSituation} className="button_review">
              Закончить обзор
            </button>
          </div>
        </div>

        <aside className="navigation_task">
          <h3>{"\u2630"} Навигация по тесту</h3>
          <div className="nav_cont_task">
            {questions.map((question, qIndex) => (
              <button
                key={`${question.questionId}-nav-${qIndex}`}
                type="button"
                className={`nav-item ${
                  question.isCorrect ? "nav-correct" : "nav-wrong"
                }`}
                onClick={() => scrollToQuestion(qIndex)}
                aria-label={`Вопрос ${qIndex + 1}`}
              >
                <span className="nav-number">{qIndex + 1}</span>
                <span
                  className={`bottom ${
                    question.isCorrect ? "bot_correct" : "bot_wrong"
                  }`}
                >
                  {question.isCorrect ? <span className="nav-check" /> : null}
                </span>
              </button>
            ))}
          </div>
          <span className="nav-action-link">Показать одну страницу</span>
          <span className="nav-action-link" onClick={handleFinishSituation}>
            Закончить обзор
          </span>
        </aside>
      </div>
    </div>
  );
};

export default TaskReviewPage;
